import "./CostosPage.css";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { CostoAgriculturaResponse } from "../../../shared/response/costos/CostoAgriculturaResponse";
import { CostoGanaderiaResponse } from "../../../shared/response/costos/CostoGanaderiaResponse";
import { CostoAspersionResponse } from "../../../shared/response/costos/CostoAspersionResponse";
import { CostoEquipoResponse } from "../../../shared/response/costos/CostoEquipoResponse";
import { CostoFotogrametriaResponse } from "../../../shared/response/costos/CostoFotogrametriaResponse";
import { CostoAgriculturaListService } from "../../../shared/services/costos/CostoAgriculturaListService";
import { CostoAgriculturaSaveService } from "../../../shared/services/costos/CostoAgriculturaSaveService";
import { CostoAgriculturaDeleteService } from "../../../shared/services/costos/CostoAgriculturaDeleteService";
import { CostoGanaderiaListService } from "../../../shared/services/costos/CostoGanaderiaListService";
import { CostoGanaderiaSaveService } from "../../../shared/services/costos/CostoGanaderiaSaveService";
import { CostoGanaderiaDeleteService } from "../../../shared/services/costos/CostoGanaderiaDeleteService";
import { CostoAspersionListService } from "../../../shared/services/costos/CostoAspersionListService";
import { CostoAspersionSaveService } from "../../../shared/services/costos/CostoAspersionSaveService";
import { CostoAspersionDeleteService } from "../../../shared/services/costos/CostoAspersionDeleteService";
import { CostoEquipoListService } from "../../../shared/services/costos/CostoEquipoListService";
import { CostoEquipoSaveService } from "../../../shared/services/costos/CostoEquipoSaveService";
import { CostoEquipoDeleteService } from "../../../shared/services/costos/CostoEquipoDeleteService";
import { CostoFotogrametriaListService } from "../../../shared/services/costos/CostoFotogrametriaListService";
import { CostoFotogrametriaSaveService } from "../../../shared/services/costos/CostoFotogrametriaSaveService";
import { ProductosMarca } from "../../productosMarca";

// ── Types ──

interface CostosData {
  agricultura: CostoAgriculturaResponse[];
  ganaderia:   CostoGanaderiaResponse[];
  protec:      CostoAspersionResponse[];
  equipos:     CostoEquipoResponse[];
  vista:       CostoFotogrametriaResponse[];
}

// ── Helpers ──

function fmt(n: number): string {
  return "$" + n.toLocaleString("es-CO");
}

function parseVal(s: string): number {
  return parseInt(s.replace(/\D/g, ""), 10) || 0;
}

function nextOrden(arr: { orden: number }[]): number {
  return arr.length === 0 ? 1 : Math.max(...arr.map((r) => r.orden)) + 1;
}

// ── Page Component ──

export class CostosPage extends BasePageComponent {
  private _data: CostosData = { agricultura: [], ganaderia: [], protec: [], equipos: [], vista: [] };

  /**
   * `_productos`: lo que vende la marca (2026-10-20). Las tarifas de lo que no vende no se muestran ni se piden: su
   * sección no existe, y las tablas y el simulador ya ignoran una sección ausente.
   */
  constructor(private readonly _productos: ProductosMarca) {
    super();
  }

  public getAppRoute(): string { return "/admin/costos"; }
  public getPageTitle(): string | null { return "Costos"; }

  /** Si la marca vende alguno de los equipos de la sección «Equipos y servicios adicionales» (RASTRO, Báscula). */
  private _conEquipos(): boolean {
    return this._productos.ofrece("rastro") || this._productos.ofrece("bascula");
  }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page costos-page";
    const conProtec = this._productos.ofrece("protec");
    const conVista = this._productos.ofrece("vista");

    pageContent.innerHTML = `
      <div class="costos-scroll">
        <div class="costos-content">

          <div class="costos-sec">
            <div class="costos-sec-hd">
              <div>
                <div class="costos-sec-hd-inner">
                  <span class="costos-sec-t">DATA AG — Agricultura</span>
                  <span class="costos-tag tag-sw">SOFTWARE</span>
                </div>
                <div class="costos-sec-s">Precio anual por rango de ha · cultivos no ganaderos</div>
              </div>
            </div>
            <div class="costos-tbl-wrap"><table class="costos-tbl"><thead><tr><th>Rango (ha)</th><th>Precio anual (COP)</th><th></th></tr></thead><tbody id="costos-tb-agri"></tbody></table></div>
          </div>

          <div class="costos-sec">
            <div class="costos-sec-hd">
              <div>
                <div class="costos-sec-hd-inner">
                  <span class="costos-sec-t">DATA AG — Ganadería</span>
                  <span class="costos-tag tag-sw">SOFTWARE</span>
                </div>
                <div class="costos-sec-s">Precio anual por rango de ha · ganadería dominante</div>
              </div>
            </div>
            <div class="costos-tbl-wrap"><table class="costos-tbl"><thead><tr><th>Rango (ha)</th><th>Precio anual (COP)</th><th></th></tr></thead><tbody id="costos-tb-gan"></tbody></table></div>
          </div>

          ${conProtec ? `<div class="costos-sec">
            <div class="costos-sec-hd">
              <div>
                <div class="costos-sec-hd-inner">
                  <span class="costos-sec-t">PROTEC — Aspersión</span>
                  <span class="costos-tag tag-sv">SERVICIO</span>
                </div>
                <div class="costos-sec-s">Precio por ha según categoría · mínimo 10 ha</div>
              </div>
            </div>
            <div class="costos-tbl-wrap"><table class="costos-tbl"><thead><tr><th>Categoría</th><th>Cultivos</th><th>Precio / ha (COP)</th><th></th></tr></thead><tbody id="costos-tb-protec"></tbody></table></div>
          </div>` : ""}

          ${this._conEquipos() ? `<div class="costos-sec">
            <div class="costos-sec-hd">
              <div>
                <div class="costos-sec-hd-inner">
                  <span class="costos-sec-t">Equipos y servicios adicionales</span>
                  <span class="costos-tag tag-eq">EQUIPO / SERVICIO</span>
                </div>
                <div class="costos-sec-s">RASTRO · Báscula · precios unitarios</div>
              </div>
            </div>
            <div class="costos-tbl-wrap"><table class="costos-tbl"><thead><tr><th>Producto</th><th>Unidad</th><th>Precio (COP)</th><th>Nota</th><th></th></tr></thead><tbody id="costos-tb-otros"></tbody></table></div>
          </div>` : ""}

          ${conVista ? `<div class="costos-sec">
            <div class="costos-sec-hd">
              <div>
                <div class="costos-sec-hd-inner">
                  <span class="costos-sec-t">VISTA — Fotogrametría</span>
                  <span class="costos-tag tag-foto">SERVICIO</span>
                </div>
                <div class="costos-sec-s">Variables del motor de cálculo · precio se genera dinámicamente por visita</div>
              </div>
            </div>
            <div class="costos-tbl-wrap"><table class="costos-tbl"><thead><tr><th>Variable</th><th>Descripción</th><th>Valor (COP)</th><th></th></tr></thead><tbody id="costos-tb-vista"></tbody></table></div>
            <div class="costos-sim-wrap">
              <div class="costos-sim-hd">Simulador de precio</div>
              <div class="costos-vista-inputs">
                <div class="costos-vi-group">
                  <div class="costos-vi-lbl">Distancia (km)</div>
                  <input class="costos-vi-input" id="costos-vi-km" type="number" value="20">
                </div>
                <div class="costos-vi-group">
                  <div class="costos-vi-lbl">Hectáreas</div>
                  <input class="costos-vi-input" id="costos-vi-ha" type="number" value="50">
                </div>
                <div class="costos-vi-group">
                  <div class="costos-vi-lbl">Tipo de vuelo</div>
                  <select class="costos-vi-sel" id="costos-vi-tipo">
                    <option value="agricultura">Agricultura</option>
                    <option value="ganadería">Ganadería</option>
                  </select>
                </div>
                <div class="costos-vi-group">
                  <div class="costos-vi-lbl">Opciones</div>
                  <label class="costos-vi-check">
                    <input type="checkbox" id="costos-vi-conteo"> Conteo de árboles
                  </label>
                </div>
              </div>
            </div>
            <div class="costos-vista-preview" id="costos-vista-preview"></div>
          </div>` : ""}

        </div>
      </div>
    `;

    this._setLoadingAll();
    this._wireSimulator();
    this._loadData();
  }

  protected override beforeDispose(): void {}

  // ── Load ──

  private _setLoadingAll(): void {
    const ids = ["costos-tb-agri", "costos-tb-gan", "costos-tb-protec", "costos-tb-otros", "costos-tb-vista"];
    for (const id of ids) {
      const tb = document.getElementById(id);
      if (tb) tb.innerHTML = `<tr><td colspan="10" style="padding:16px;color:var(--sx-text-tertiary);font-size:var(--sx-text-xs)">Cargando…</td></tr>`;
    }
  }

  private async _loadData(): Promise<void> {
    // Los listados de lo que la marca no vende no se piden: quedan vacíos, como su sección.
    const [agri, gan, protec, equipos, vista] = await Promise.all([
      CostoAgriculturaListService.instance.get().catch(() => [] as CostoAgriculturaResponse[]),
      CostoGanaderiaListService.instance.get().catch(() => [] as CostoGanaderiaResponse[]),
      this._productos.ofrece("protec")
        ? CostoAspersionListService.instance.get().catch(() => [] as CostoAspersionResponse[])
        : Promise.resolve([] as CostoAspersionResponse[]),
      this._conEquipos()
        ? CostoEquipoListService.instance.get().catch(() => [] as CostoEquipoResponse[])
        : Promise.resolve([] as CostoEquipoResponse[]),
      this._productos.ofrece("vista")
        ? CostoFotogrametriaListService.instance.get().catch(() => [] as CostoFotogrametriaResponse[])
        : Promise.resolve([] as CostoFotogrametriaResponse[]),
    ]);

    this._data = {
      agricultura: agri.sort((a, b)  => a.orden - b.orden),
      ganaderia:   gan.sort((a, b)   => a.orden - b.orden),
      protec:      protec.sort((a, b) => a.orden - b.orden),
      equipos:     equipos.sort((a, b) => a.orden - b.orden),
      vista:       vista.sort((a, b) => a.orden - b.orden),
    };

    this._renderAllTables();
    this._simVISTA();
  }

  // ── Render helpers ──

  private _priceCell(id: string, val: number | null, isNeg: boolean): string {
    return `<td id="${id}"><div class="costos-price-cell">
      <span class="costos-price-val">${isNeg || val == null ? '<span class="costos-pill p-na">Negociación</span>' : fmt(val)}</span>
      ${!isNeg && val != null ? `<div class="costos-edit-btn" data-cell-id="${id}"><svg viewBox="0 0 24 24"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg></div>` : ""}
    </div></td>`;
  }

  private _delBtn(id: string): string {
    return `<td><div class="costos-del-btn" data-del-id="${id}"><svg viewBox="0 0 24 24"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg></div></td>`;
  }

  private _addRowBtn(section: string): string {
    return `<tr class="costos-add-row"><td colspan="10"><button class="costos-add-row-btn" data-add="${section}"><svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>Agregar</button></td></tr>`;
  }

  // ── Render tables ──

  private _renderAllTables(): void {
    this._renderAgri();
    this._renderGan();
    this._renderProtec();
    this._renderOtros();
    this._renderVISTA();
  }

  private _renderAgri(): void {
    const tb = document.getElementById("costos-tb-agri");
    if (!tb) return;
    tb.innerHTML = this._data.agricultura
      .map((r, i) => `<tr>
        <td class="costos-td-strong">${r.rango}</td>
        ${this._priceCell("costos-ap" + i, r.precioAnual, r.isNegociacion)}
        ${this._delBtn(r.id)}
      </tr>`)
      .join("") + this._addRowBtn("agricultura");

    this._data.agricultura.forEach((r, i) => {
      const editBtn = tb.querySelector<HTMLElement>(`.costos-edit-btn[data-cell-id="costos-ap${i}"]`);
      editBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        this._startEdit(`costos-ap${i}`, r.precioAnual!, async (v) => {
          const saved = await CostoAgriculturaSaveService.instance.save(
            { id: r.id, rango: r.rango, topeMaximoHa: r.topeMaximoHa, precioAnual: v, orden: r.orden },
          );
          this._data.agricultura[i] = saved;
        });
      });

      const delBtn = tb.querySelector<HTMLElement>(`.costos-del-btn[data-del-id="${r.id}"]`);
      delBtn?.addEventListener("click", (e) => { e.stopPropagation(); this._deleteItem(r.id, delBtn, () => {
        this._data.agricultura = this._data.agricultura.filter((x) => x.id !== r.id);
        this._renderAgri();
      }, CostoAgriculturaDeleteService.instance); });
    });

    tb.querySelector<HTMLElement>(".costos-add-row-btn")?.addEventListener("click", (e) => {
      e.stopPropagation(); this._addRowInline("agricultura");
    });
  }

  private _renderGan(): void {
    const tb = document.getElementById("costos-tb-gan");
    if (!tb) return;
    tb.innerHTML = this._data.ganaderia
      .map((r, i) => `<tr>
        <td class="costos-td-strong">${r.rango}</td>
        ${this._priceCell("costos-gp" + i, r.precioAnual, r.isNegociacion)}
        ${this._delBtn(r.id)}
      </tr>`)
      .join("") + this._addRowBtn("ganaderia");

    this._data.ganaderia.forEach((r, i) => {
      const editBtn = tb.querySelector<HTMLElement>(`.costos-edit-btn[data-cell-id="costos-gp${i}"]`);
      editBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        this._startEdit(`costos-gp${i}`, r.precioAnual!, async (v) => {
          const saved = await CostoGanaderiaSaveService.instance.save(
            { id: r.id, rango: r.rango, topeMaximoHa: r.topeMaximoHa, precioAnual: v, orden: r.orden },
          );
          this._data.ganaderia[i] = saved;
        });
      });

      const delBtn = tb.querySelector<HTMLElement>(`.costos-del-btn[data-del-id="${r.id}"]`);
      delBtn?.addEventListener("click", (e) => { e.stopPropagation(); this._deleteItem(r.id, delBtn, () => {
        this._data.ganaderia = this._data.ganaderia.filter((x) => x.id !== r.id);
        this._renderGan();
      }, CostoGanaderiaDeleteService.instance); });
    });

    tb.querySelector<HTMLElement>(".costos-add-row-btn")?.addEventListener("click", (e) => {
      e.stopPropagation(); this._addRowInline("ganaderia");
    });
  }

  private _renderProtec(): void {
    const tb = document.getElementById("costos-tb-protec");
    if (!tb) return;
    tb.innerHTML = this._data.protec
      .map((r, i) => {
        const cultivosText = r.cultivos.map((c) => c.nombre).join(", ") || "—";
        return `<tr>
          <td><span class="costos-pill p-ok">${r.categoria}</span></td>
          <td class="costos-td-muted">${cultivosText}</td>
          ${this._priceCell("costos-pp" + i, r.precioPorHa, false)}
          ${this._delBtn(r.id)}
        </tr>`;
      })
      .join("") + this._addRowBtn("protec");

    this._data.protec.forEach((r, i) => {
      const editBtn = tb.querySelector<HTMLElement>(`.costos-edit-btn[data-cell-id="costos-pp${i}"]`);
      editBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        this._startEdit(`costos-pp${i}`, r.precioPorHa, async (v) => {
          const saved = await CostoAspersionSaveService.instance.save(
            { id: r.id, categoria: r.categoria, precioPorHa: v, orden: r.orden, cultivoIds: r.cultivos.map((c) => c.cultivoId) },
          );
          this._data.protec[i] = saved;
        });
      });

      const delBtn = tb.querySelector<HTMLElement>(`.costos-del-btn[data-del-id="${r.id}"]`);
      delBtn?.addEventListener("click", (e) => { e.stopPropagation(); this._deleteItem(r.id, delBtn, () => {
        this._data.protec = this._data.protec.filter((x) => x.id !== r.id);
        this._renderProtec();
      }, CostoAspersionDeleteService.instance); });
    });

    tb.querySelector<HTMLElement>(".costos-add-row-btn")?.addEventListener("click", (e) => {
      e.stopPropagation(); this._addRowInline("protec");
    });
  }

  private _renderOtros(): void {
    const tb = document.getElementById("costos-tb-otros");
    if (!tb) return;
    tb.innerHTML = this._data.equipos
      .map((r, i) => `<tr>
        <td class="costos-td-strong">${r.nombre}</td>
        <td class="costos-td-muted">${r.unidad}</td>
        ${this._priceCell("costos-op" + i, r.precio, false)}
        <td class="costos-td-muted">${r.nota ?? ""}</td>
        ${this._delBtn(r.id)}
      </tr>`)
      .join("") + this._addRowBtn("equipos");

    this._data.equipos.forEach((r, i) => {
      const editBtn = tb.querySelector<HTMLElement>(`.costos-edit-btn[data-cell-id="costos-op${i}"]`);
      editBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        this._startEdit(`costos-op${i}`, r.precio, async (v) => {
          const saved = await CostoEquipoSaveService.instance.save(
            { id: r.id, nombre: r.nombre, unidad: r.unidad, precio: v, nota: r.nota, orden: r.orden },
          );
          this._data.equipos[i] = saved;
        });
      });

      const delBtn = tb.querySelector<HTMLElement>(`.costos-del-btn[data-del-id="${r.id}"]`);
      delBtn?.addEventListener("click", (e) => { e.stopPropagation(); this._deleteItem(r.id, delBtn, () => {
        this._data.equipos = this._data.equipos.filter((x) => x.id !== r.id);
        this._renderOtros();
      }, CostoEquipoDeleteService.instance); });
    });

    tb.querySelector<HTMLElement>(".costos-add-row-btn")?.addEventListener("click", (e) => {
      e.stopPropagation(); this._addRowInline("equipos");
    });
  }

  private _renderVISTA(): void {
    const tb = document.getElementById("costos-tb-vista");
    if (!tb) return;
    tb.innerHTML = this._data.vista
      .map((r, i) => `<tr>
        <td class="costos-td-strong">${r.variable}</td>
        <td class="costos-td-desc">${r.descripcion}</td>
        ${this._priceCell("costos-vp" + i, r.valor, false)}
        <td></td>
      </tr>`)
      .join("");

    this._data.vista.forEach((r, i) => {
      const editBtn = tb.querySelector<HTMLElement>(`.costos-edit-btn[data-cell-id="costos-vp${i}"]`);
      editBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        this._startEdit(`costos-vp${i}`, r.valor, async (v) => {
          const saved = await CostoFotogrametriaSaveService.instance.save({ id: r.id, valor: v });
          this._data.vista[i] = saved;
          this._simVISTA();
        });
      });
    });
  }

  // ── Inline edit ──

  private _startEdit(
    cellId: string,
    currentVal: number,
    onSave: (v: number) => Promise<void>,
  ): void {
    const td = document.getElementById(cellId);
    if (!td) return;
    td.innerHTML = `<div style="display:flex;align-items:center;gap:5px">
      <input class="costos-price-input" value="${currentVal}" type="text" id="costos-ei" oninput="this.value=this.value.replace(/[^0-9]/g,'')">
      <button class="sx-btn sx-btn-primary sx-btn-sm" id="costos-ei-ok">OK</button>
      <button class="sx-btn sx-btn-ghost sx-btn-sm" id="costos-ei-cancel">✕</button>
    </div>`;
    const input = document.getElementById("costos-ei") as HTMLInputElement;
    const okBtn = document.getElementById("costos-ei-ok") as HTMLButtonElement;
    input?.focus();
    input?.select();

    okBtn.onclick = async () => {
      const v = parseInt(input.value, 10) || 0;
      okBtn.disabled = true;
      try {
        await onSave(v);
        this._renderAllTables();
        this._simVISTA();
      } catch (err) {
        errorMessageComponent.addError("Error al guardar", (err as Error).message);
        this._renderAllTables();
        this._simVISTA();
      }
    };

    document.getElementById("costos-ei-cancel")!.onclick = () => {
      this._renderAllTables();
      this._simVISTA();
    };
  }

  // ── Delete helper ──

  private _deleteItem(
    id: string,
    btn: HTMLElement,
    onDeleted: () => void,
    service: { delete(id: string): Promise<void> },
  ): void {
    btn.style.opacity = "0.4";
    btn.style.pointerEvents = "none";
    service.delete(id).then(() => {
      onDeleted();
    }).catch((err: Error) => {
      errorMessageComponent.addError("Error al eliminar", err.message);
      btn.style.opacity = "";
      btn.style.pointerEvents = "";
    });
  }

  // ── Inline add row ──

  private _addRowInline(section: string): void {
    const tbIdMap: Record<string, string> = {
      agricultura: "costos-tb-agri",
      ganaderia:   "costos-tb-gan",
      protec:      "costos-tb-protec",
      equipos:     "costos-tb-otros",
    };
    const tbId = tbIdMap[section];
    if (!tbId) return;
    const tb = document.getElementById(tbId);
    if (!tb) return;

    const addRow = tb.querySelector(".costos-add-row");
    const nr = document.createElement("tr");
    nr.className = "costos-new-row";

    if (section === "agricultura" || section === "ganaderia") {
      nr.innerHTML = `
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Rango (ha)</div><input class="costos-ni" id="costos-ni-0" placeholder="Ej: 0 – 50 ha"></td>
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Precio anual (COP)</div><input class="costos-ni" id="costos-ni-1" placeholder="Precio o dejar vacío para negociación"></td>
        <td><div style="display:flex;gap:4px;padding-top:18px">
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="costos-confirm-add">Agregar</button>
          <button class="sx-btn sx-btn-ghost sx-btn-sm" id="costos-cancel-add">✕</button>
        </div></td>`;
    } else if (section === "protec") {
      nr.innerHTML = `
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Categoría</div><input class="costos-ni" id="costos-ni-0" placeholder="Ej: Cat 7"></td>
        <td></td>
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Precio / ha</div><input class="costos-ni" id="costos-ni-1" placeholder="COP"></td>
        <td><div style="display:flex;gap:4px;padding-top:18px">
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="costos-confirm-add">Agregar</button>
          <button class="sx-btn sx-btn-ghost sx-btn-sm" id="costos-cancel-add">✕</button>
        </div></td>`;
    } else if (section === "equipos") {
      nr.innerHTML = `
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Producto</div><input class="costos-ni" id="costos-ni-0" placeholder="Nombre"></td>
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Unidad</div><input class="costos-ni" id="costos-ni-1" placeholder="por unidad"></td>
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Precio</div><input class="costos-ni" id="costos-ni-2" placeholder="COP"></td>
        <td><div class="costos-vi-lbl" style="margin-bottom:3px">Nota</div><input class="costos-ni" id="costos-ni-3" placeholder="+ IVA 19%"></td>
        <td><div style="display:flex;gap:4px;padding-top:18px">
          <button class="sx-btn sx-btn-primary sx-btn-sm" id="costos-confirm-add">Agregar</button>
          <button class="sx-btn sx-btn-ghost sx-btn-sm" id="costos-cancel-add">✕</button>
        </div></td>`;
    }

    tb.insertBefore(nr, addRow ?? null);
    (document.getElementById("costos-ni-0") as HTMLInputElement | null)?.focus();

    const confirmBtn = document.getElementById("costos-confirm-add") as HTMLButtonElement;
    confirmBtn.onclick = async () => {
      const vals: string[] = [];
      let i = 0;
      while (document.getElementById(`costos-ni-${i}`)) {
        vals.push(((document.getElementById(`costos-ni-${i}`) as HTMLInputElement).value.trim()));
        i++;
      }
      confirmBtn.disabled = true;
      await this._confirmAdd(section, vals);
    };

    document.getElementById("costos-cancel-add")!.onclick = () => this._renderAllTables();
  }

  private async _confirmAdd(section: string, vals: string[]): Promise<void> {
    try {
      if (section === "agricultura") {
        const precio = parseVal(vals[1]);
        const saved = await CostoAgriculturaSaveService.instance.save({
          id: null, rango: vals[0] || "Nuevo rango",
          topeMaximoHa: null, precioAnual: precio > 0 ? precio : null,
          orden: nextOrden(this._data.agricultura),
        });
        this._data.agricultura.push(saved);
      } else if (section === "ganaderia") {
        const precio = parseVal(vals[1]);
        const saved = await CostoGanaderiaSaveService.instance.save({
          id: null, rango: vals[0] || "Nuevo rango",
          topeMaximoHa: null, precioAnual: precio > 0 ? precio : null,
          orden: nextOrden(this._data.ganaderia),
        });
        this._data.ganaderia.push(saved);
      } else if (section === "protec") {
        const saved = await CostoAspersionSaveService.instance.save({
          id: null, categoria: vals[0] || "Nueva",
          precioPorHa: parseVal(vals[1]), cultivoIds: [],
          orden: nextOrden(this._data.protec),
        });
        this._data.protec.push(saved);
      } else if (section === "equipos") {
        const saved = await CostoEquipoSaveService.instance.save({
          id: null, nombre: vals[0] || "Nuevo producto",
          unidad: vals[1] || "por unidad", precio: parseVal(vals[2]),
          nota: vals[3] || null, orden: nextOrden(this._data.equipos),
        });
        this._data.equipos.push(saved);
      }
      this._renderAllTables();
    } catch (err) {
      errorMessageComponent.addError("Error al guardar", (err as Error).message);
      this._renderAllTables();
    }
  }

  // ── VISTA Simulator ──

  private _wireSimulator(): void {
    for (const id of ["costos-vi-km", "costos-vi-ha", "costos-vi-tipo", "costos-vi-conteo"]) {
      const el = document.getElementById(id);
      if (el) {
        el.addEventListener("input", () => this._simVISTA());
        el.addEventListener("change", () => this._simVISTA());
      }
    }
  }

  private _simVISTA(): void {
    const kmEl    = document.getElementById("costos-vi-km")    as HTMLInputElement | null;
    const haEl    = document.getElementById("costos-vi-ha")    as HTMLInputElement | null;
    const tipoEl  = document.getElementById("costos-vi-tipo")  as HTMLSelectElement | null;
    const conteoEl = document.getElementById("costos-vi-conteo") as HTMLInputElement | null;
    const preview = document.getElementById("costos-vista-preview");
    if (!kmEl || !haEl || !tipoEl || !conteoEl || !preview) return;

    const V = this._data.vista;
    if (V.length < 5) { preview.innerHTML = ""; return; }

    const km     = parseFloat(kmEl.value) || 0;
    const ha     = parseFloat(haEl.value) || 0;
    const tipo   = tipoEl.value;
    const conteo = conteoEl.checked;

    const base       = V[0].valor;
    const transporte = Math.round((km * 2 / 25) * V[1].valor);
    const precioHa   = tipo === "ganadería" ? V[3].valor : V[2].valor;
    const vuelo      = Math.round(ha * precioHa);
    const cnt        = conteo ? Math.round(ha * V[4].valor) : 0;
    const total      = base + transporte + vuelo + cnt;

    preview.innerHTML = `
      <div class="costos-vp-title">Estimado para ${ha} ha · ${km} km · ${tipo}${conteo ? " + conteo" : ""}</div>
      <div class="costos-vp-grid">
        <div class="costos-vp-row"><span class="costos-vp-lbl">Tarifa base</span><span class="costos-vp-val">${fmt(base)}</span></div>
        <div class="costos-vp-row"><span class="costos-vp-lbl">Transporte</span><span class="costos-vp-val">${fmt(transporte)}</span></div>
        <div class="costos-vp-row"><span class="costos-vp-lbl">Vuelo (${fmt(precioHa)}/ha)</span><span class="costos-vp-val">${fmt(vuelo)}</span></div>
        ${conteo ? `<div class="costos-vp-row"><span class="costos-vp-lbl">Conteo árboles</span><span class="costos-vp-val">${fmt(cnt)}</span></div>` : "<div></div>"}
      </div>
      <div class="costos-vp-total"><span class="costos-vp-total-lbl">Total estimado</span><span class="costos-vp-total-val">${fmt(total)}</span></div>`;
  }
}
